import React from 'react';
import { connect } from 'react-redux';

import Content from '../Content';
import RefRenderer from '../Renderer';

import { IRecordById, IFieldById } from '../../../../../types/core';
import { IRefTableColumn, ITableConfig } from '../../../../../types/space';
import { ListStyleType } from '../../../../../types/space';

import { Container, RightSide, LeftSide } from './styled';
import { checkIfLookupIsLookingAtRecordReference } from '../../../../../modules/core/helpers';
import { ColumnType } from '../../../../../modules/core/graphql/types';
import { IRootState } from '../../../../../modules/stores';

interface IProps {
  record: IRecordById;
  fields: IFieldById[];
  // *ONLY* columns that are record refs should have another tableColumn nested inside
  tableColumns: IRefTableColumn[];
  listStyle: ListStyleType;
  showRefAsText: boolean;
  visibleNodes: { [key: string]: string };
  depth?: number;
  spaceId: string;
}

// Look at ../Row for a guide on how the recursiveness works.
const Item: React.FC<IProps> = ({
  fields,
  record,
  tableColumns,
  listStyle,
  showRefAsText,
  visibleNodes,
  depth,
  spaceId,
}) => {
  // force show reference as text if at depth limit
  const refAsText = depth && depth > 2 ? true : showRefAsText;

  const Comp =
    listStyle === ListStyleType.None ? 'div' : listStyle === ListStyleType.Numbers ? 'li' : 'li';

  return (
    <>
      {(() => (
        // So annoying, not how to get this tslint error away.
        // @ts-ignore
        <Comp>
          <Container>
            <RightSide>
              {fields.slice(0, 1).map((refField, index) => {
                // Protection, most likely happening because of permissions
                if (!refField) return null;

                const isNodeVisible =
                  (refField.type === ColumnType.RECORD_REFERENCE ||
                    refField.type === ColumnType.SUBTABLE) &&
                  visibleNodes[refField.typeOptions.foreignTableId];

                if (
                  (refField.type === ColumnType.RECORD_REFERENCE ||
                    refField.type === ColumnType.SUBTABLE) &&
                  !refAsText &&
                  isNodeVisible
                )
                  return null;

                return (
                  <Content
                    field={refField}
                    record={record}
                    spaceId={spaceId}
                    key={`fieldIdItem-${index}`}
                    depth={depth}
                    showRefAsText
                  />
                );
              })}
            </RightSide>
            <LeftSide>
              {fields.slice(1).map((refField, index) => {
                // Protection, most likely happening because of permissions
                if (!refField) return null;

                const isNodeVisible =
                  (refField.type === ColumnType.RECORD_REFERENCE ||
                    refField.type === ColumnType.SUBTABLE) &&
                  visibleNodes[refField.typeOptions.foreignTableId];

                if (
                  (refField.type === ColumnType.RECORD_REFERENCE ||
                    refField.type === ColumnType.SUBTABLE) &&
                  !refAsText &&
                  isNodeVisible
                )
                  return null;

                return (
                  <Content
                    field={refField}
                    record={record}
                    key={`fieldIdItem-${index}`}
                    depth={depth}
                    showRefAsText
                    spaceId={spaceId}
                  />
                );
              })}
            </LeftSide>
          </Container>
        </Comp>
      ))()}

      {fields.map((refField, index) => {
        // Protection, most likely happening because of permissions
        if (!refField) return null;

        const isNodeVisible =
          (refField.type === ColumnType.RECORD_REFERENCE ||
            refField.type === ColumnType.SUBTABLE) &&
          visibleNodes[refField.typeOptions.foreignTableId];

        if (
          !(
            ((refField.type === ColumnType.RECORD_REFERENCE ||
              refField.type === ColumnType.SUBTABLE) &&
              !refAsText &&
              isNodeVisible) ||
            (checkIfLookupIsLookingAtRecordReference(refField) && !refAsText)
          )
        )
          return null;

        const currentTableColumnConfig = tableColumns.find((col) => col.id === refField.id);
        if (!currentTableColumnConfig) return null;

        return (
          <RefRenderer
            key={`fieldIdRec-${index}`}
            config={currentTableColumnConfig as ITableConfig}
            fieldId={refField.id}
            recordId={record.id}
            isLookup={checkIfLookupIsLookingAtRecordReference(refField)}
            depth={depth}
            spaceId={spaceId}
          />
        );
      })}
    </>
  );
};

const mapState = ({ core }: IRootState) => ({
  visibleNodes: core.visibleNodes,
});

export default connect(mapState)(Item);
